import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CheckCircle2, Edit } from "lucide-react";
import { cn } from "@/lib/utils";
import { ReviewStatusBadge } from "./ReviewStatusBadge";

type Mcq = {
  id: string;
  topic_id: string | null;
  question: string;
  options: string[];
  correct_index: number;
  explanation?: string | null;
  difficulty: number;
  tags: string[] | null;
  review_status?: 'pending' | 'approved' | 'needs_revision';
  last_reviewed_at?: string | null;
};

type MCQPreviewModalProps = {
  mcq: Mcq | null;
  topicName?: string;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
};

const DifficultyStars = ({ difficulty }: { difficulty: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className={cn("text-xl", i < difficulty ? "text-yellow-500" : "text-muted")}>
        ★
      </span>
    ))}
  </div>
);

export const MCQPreviewModal = ({ mcq, topicName, open, onClose, onEdit }: MCQPreviewModalProps) => {
  if (!mcq) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <DialogTitle>MCQ Preview</DialogTitle>
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Meta information */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Status:</span>
              <ReviewStatusBadge status={mcq.review_status || 'pending'} />
            </div>
            {topicName && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Topic:</span>
                <Badge variant="secondary">{topicName}</Badge>
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Difficulty:</span>
              <DifficultyStars difficulty={mcq.difficulty} />
            </div>
          </div>

          {mcq.tags && mcq.tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-muted-foreground">Tags:</span>
              {mcq.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          <Separator />

          {/* Question */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Question</h3>
            <p className="text-base leading-relaxed">{mcq.question}</p>
          </div>

          <Separator />

          {/* Options */}
          <div className="space-y-3">
            <h3 className="font-semibold text-lg">Options</h3>
            <div className="grid gap-3">
              {mcq.options.map((option, idx) => (
                <div
                  key={idx}
                  className={cn(
                    "p-4 rounded-lg border-2 transition-colors",
                    idx === mcq.correct_index
                      ? "border-green-500 bg-green-50 dark:bg-green-950"
                      : "border-border bg-muted/50"
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={cn(
                        "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm",
                        idx === mcq.correct_index
                          ? "bg-green-500 text-white"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {String.fromCharCode(65 + idx)}
                    </div>
                    <p className="flex-1 text-sm pt-1">{option}</p>
                    {idx === mcq.correct_index && (
                      <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Explanation */}
          {mcq.explanation && (
            <>
              <Separator />
              <div className="space-y-3">
                <h3 className="font-semibold text-lg">Explanation</h3>
                <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-900">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{mcq.explanation}</p>
                </div>
              </div>
            </>
          )}

          {!mcq.explanation && (
            <div className="p-4 rounded-lg bg-muted border border-dashed">
              <p className="text-sm text-muted-foreground text-center">No explanation available</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
